import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { Documento } from '@/types/documento'

interface Props {
  /** Documento vigente a substituir; nulo = diálogo fechado. */
  documento: Documento | null
  enviando?: boolean
  onCancelar: () => void
  onConfirmar: (arquivo: File, motivo: string) => void
}

const ACEITOS = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.png'
const MAX_MOTIVO = 200

/**
 * "Enviar arquivo substituto" na resposta de pendência (Onda 4, L11/Q38). A resposta anterior
 * não é apagada: o novo arquivo vira a versão vigente e o anterior fica na trilha com autor,
 * data e motivo — por isso o motivo é obrigatório (o backend também exige).
 */
export const ArquivoSubstitutoDialog: FC<Props> = ({ documento, enviando = false, onCancelar, onConfirmar }) => {
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [motivo, setMotivo] = useState('')

  useEffect(() => {
    if (documento) {
      setArquivo(null)
      setMotivo('')
    }
  }, [documento])

  const pronto = !!arquivo && motivo.trim().length > 0 && !enviando

  return (
    <Dialog open={!!documento} onClose={enviando ? undefined : onCancelar} fullWidth maxWidth="sm">
      <DialogTitle>Enviar arquivo substituto</DialogTitle>
      <DialogContent>
        {documento && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Substitui <strong>{documento.nome}</strong>. O arquivo atual não é apagado: fica nas versões
            anteriores, com data, autor e o motivo informado abaixo.
          </Typography>
        )}
        <Box sx={{ mb: 2 }}>
          <Button component="label" size="small" variant="outlined" startIcon={<AttachFileIcon />}>
            Selecionar arquivo
            <input
              type="file"
              hidden
              accept={ACEITOS}
              aria-label="Arquivo substituto"
              onChange={(e) => {
                setArquivo(e.target.files?.[0] ?? null)
                e.target.value = ''
              }}
            />
          </Button>
          {arquivo && <Chip size="small" label={arquivo.name} sx={{ ml: 1 }} onDelete={() => setArquivo(null)} />}
        </Box>
        <TextField
          label="Motivo da substituição"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO))}
          required
          fullWidth
          multiline
          minRows={2}
          helperText={`${motivo.length}/${MAX_MOTIVO}`}
          placeholder="Ex.: enviei o período errado"
        />
        <Alert severity="info" icon={false} sx={{ mt: 2 }}>
          Possível enquanto a pendência estiver aberta. Depois que a Originei começa a validar, só a curadoria
          reabre a pendência.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelar} color="inherit" disabled={enviando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!pronto}
          onClick={() => arquivo && onConfirmar(arquivo, motivo.trim())}
        >
          {enviando ? 'Enviando…' : 'Enviar substituto'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ArquivoSubstitutoDialog
